import { useEffect, useState } from "react";
import productImage from "@/assets/product-package.jpeg";
import { SubtleWaves } from "@/components/ui/waves";
import { useSiteContent } from "@/hooks/useSiteContent";

export const Mission = () => {
  const [scrollY, setScrollY] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { content } = useSiteContent('mission');

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    
    // Preload the image after initial render
    const img = new Image();
    img.src = productImage;
    img.onload = () => setImageLoaded(true);
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section id="mission" className="relative py-24 overflow-hidden section-lavender">
      {/* Soft Feminine Background */}
      <div className="absolute inset-0">
        <SubtleWaves className="opacity-40" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Premium Floating Product Image */}
          <div className="relative">
            <div 
              className="floating-3d"
              style={{
                transform: `translateY(${scrollY * 0.1}px) rotateX(3deg) rotateY(8deg)`,
              }}
            >
              <img
                src={productImage}
                alt="Lunssence Blue Lotus Tea Package"
                className="w-full max-w-md mx-auto rounded-3xl shadow-floating transform-gpu"
                width="1024"
                height="1536"
                loading="eager"
                decoding="async"
                style={{ 
                  boxShadow: '0 20px 40px hsl(var(--lavender) / 0.3), 0 0 25px hsl(var(--gold) / 0.15)',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in'
                }}
              />
              
              {/* Soft floating glow effect */}
              <div className="absolute inset-0 bg-gradient-gold rounded-3xl blur-2xl transform scale-110 -z-10 opacity-15 animate-luxury-pulse" />
            </div>
          </div>

          {/* Mission Content */}
          <div className="space-y-8">
            <div className="animate-on-scroll">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold mb-6 text-glow">
                {content?.title || 'Onze Missie'}
              </h2>
              
              <div className="w-20 h-1 bg-gradient-gold mb-8 rounded-full shadow-gold" />
              
              <p className="text-lg text-foreground leading-relaxed mb-6">
                {content?.description || 'Bij Lunssence geloven we in de kracht van natuurlijke ingrediënten en eeuwenoude rituelen.'}
              </p>
              
              {content?.metadata?.highlights && Array.isArray(content.metadata.highlights) && (
                <div className="space-y-4">
                  {content.metadata.highlights.map((highlight: string, idx: number) => (
                    <p key={idx} className="text-lg text-foreground leading-relaxed">
                      • {highlight}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Premium Brand Values */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="text-center animate-on-scroll [animation-delay:0.2s]">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
                  <span className="text-primary font-bold text-xl">🌿</span>
                </div>
                <p className="text-sm font-semibold text-foreground">100% Natuurlijk</p>
              </div>
              
              <div className="text-center animate-on-scroll [animation-delay:0.4s]">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
                  <span className="text-primary font-bold text-xl">✨</span>
                </div>
                <p className="text-sm font-semibold text-foreground">Premium Kwaliteit</p>
              </div>
              
              <div className="text-center animate-on-scroll [animation-delay:0.6s]">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
                  <span className="text-primary font-bold text-xl">🌙</span>
                </div>
                <p className="text-sm font-semibold text-foreground">Spiritueel Ritueel</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};